import { useState } from "react";
import { ClipboardCheck, Copy, Download, FileText, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MailComposer } from "@/pages/vesta/VestaMail";

const pill = "inline-flex items-center gap-1.5 rounded-full border border-neutral-700 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50";

export const openChecklistPdf = async (id, win) => {
  const w = win || window.open("", "_blank");
  try { const r = await api.get(`/vesta/voice/relazioni/${id}/pdf`, { responseType: "blob", timeout: 60000 }); w.location.href = URL.createObjectURL(r.data); }
  catch { w?.close(); toast.error("PDF non disponibile"); }
};

export function ChecklistView({ c }) {
  const [done, setDone] = useState({});
  const tot = (c.fasi || []).reduce((a, f) => a + f.voci.length, 0);
  const n = Object.values(done).filter(Boolean).length;
  const copy = () => navigator.clipboard.writeText(c.testo || "").then(() => toast.success("Check-list copiata"));
  return (
    <div className="space-y-3" data-testid="checklist-view">
      {c.sintesi && <p className="text-xs leading-relaxed text-neutral-300">{c.sintesi}</p>}
      <div className="flex items-center gap-2 text-[11px] text-neutral-400">
        <span data-testid="checklist-progress">{n}/{tot} controlli</span>
        <span className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-800"><span className="block h-full bg-[#C17F59] transition-all" style={{ width: `${tot ? (n / tot) * 100 : 0}%` }} /></span>
        {c.testo && <button type="button" onClick={copy} className="inline-flex items-center gap-1 hover:text-white" data-testid="checklist-copy"><Copy className="h-3.5 w-3.5" />Copia</button>}
      </div>
      {(c.fasi || []).map((f, i) => (
        <section key={f.titolo} data-testid={`checklist-fase-${i}`}>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#C17F59]">{i + 1}. {f.titolo}</div>
          <ul className="space-y-1">{f.voci.map((v, j) => {
            const k = `${i}-${j}`;
            return (
              <li key={k}><label className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1 text-xs text-neutral-200 hover:bg-white/5">
                <input type="checkbox" checked={!!done[k]} onChange={() => setDone({ ...done, [k]: !done[k] })} className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#C17F59]" data-testid={`checklist-item-${k}`} />
                <span className={done[k] ? "text-neutral-500 line-through" : ""}>{v.testo}{v.misura && <span className="ml-1 text-neutral-500">· ____ {v.unita || ""}</span>}</span>
              </label></li>
            );
          })}</ul>
        </section>
      ))}
    </div>
  );
}

export function ChecklistButton({ base, params, plant, accent }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [c, setC] = useState(null);
  const [mail, setMail] = useState(false);
  const make = async (formato) => {
    setBusy(formato);
    const w = formato === "pdf" ? window.open("", "_blank") : null;
    try {
      const r = await api.post(`${base}/plants/${encodeURIComponent(plant.id)}/checklist`, { formato }, { params, timeout: 150000 });
      setC(r.data);
      if (formato === "pdf") openChecklistPdf(r.data.id, w);
    } catch (e) { w?.close(); toast.error(e.response?.data?.detail || "Check-list non disponibile"); }
    setBusy(null);
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="checklist-open-btn"
        className="flex w-full items-center justify-center gap-2 rounded-full border border-emerald-400/60 py-2.5 text-sm font-bold text-emerald-300 transition-colors hover:bg-white/5">
        <ClipboardCheck className="h-4 w-4" />Check-list per analizzare l'impianto
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="checklist-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-xl" style={{ color: accent }}><ClipboardCheck className="h-6 w-6" />Check-list · {plant.nome}</DialogTitle>
            <DialogDescription className="text-[11px] uppercase tracking-widest text-neutral-500">Dai dati reali, dagli allarmi, dai manuali e dalla base di conoscenza Torri</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={pill} disabled={!!busy} onClick={() => make("testo")} data-testid="checklist-make-testo">{busy === "testo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}Testuale</button>
            <button type="button" className={pill} disabled={!!busy} onClick={() => (c ? openChecklistPdf(c.id) : make("pdf"))} data-testid="checklist-make-pdf">{busy === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}PDF</button>
            {c && <button type="button" className={pill} onClick={() => { setOpen(false); setMail(true); }} data-testid="checklist-email"><Mail className="h-3.5 w-3.5" />Invia per email</button>}
          </div>
          {busy && !c && <p className="text-xs text-neutral-400" data-testid="checklist-loading">Preparo la check-list con i dati dell'impianto e i manuali Torri…</p>}
          {c && <ChecklistView c={c} />}
        </DialogContent>
      </Dialog>
      {mail && c && <MailComposer draft={{ subject: c.titolo, body: `Buongiorno,\n\nin allegato la check-list per l'analisi dell'impianto ${plant.nome}, preparata con Ai VESTA.\n\nCordiali saluti` }}
        onClose={() => setMail(false)} sendUrl={`/vesta/voice/relazioni/${c.id}/email`} attachment="Check-list (PDF)" />}
    </>
  );
}
